import { ChevronDown, ChevronUp, Search, X } from "lucide-react";
import * as React from "react";
import { ToneDot, type Tone } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { UserSearchMore, useUserSearch } from "@/features/user-search";
import { ALL_STATUSES, statusInfo, type DateRange } from "@/lib/appointments";
import { staffName } from "@/lib/calendars";
import { withPicked } from "@/lib/paging";
import { useCalendars, useUsersById } from "@/lib/queries";
import type { AppointmentStatus } from "@/lib/types";
import { cn } from "@/lib/utils";

export type ViewType = "all" | "callbacks" | "appointments" | "blocks";

/** What both the calendar view and the list show (ACL-292): one set of filters, so the two views
 *  always agree. Empty lists mean "not narrowed". The days (ACL-598) are the list's only: the
 *  calendar has its own day, week and month to move through. */
export interface ViewFilters extends DateRange {
  type: ViewType;
  userIds: number[];
  calendarIds: number[];
  /** Appointment statuses; empty is every status. Blocks and callbacks have none, so it narrows
   *  appointments only. */
  statuses: string[];
}

export const NO_FILTERS: ViewFilters = { type: "all", userIds: [], calendarIds: [], statuses: [], from: null, to: null };

/** How many filters are on; the days count once, and only where they apply (`withDates`). */
export function filterCount(f: ViewFilters, withDates: boolean): number {
  const dated = withDates && Boolean(f.from || f.to);
  return f.userIds.length + f.calendarIds.length + f.statuses.length + (f.type === "all" ? 0 : 1) + Number(dated);
}

const SHOWN = 5;

/**
 * GoHighLevel's "Manage view" panel: what to show (callbacks, appointments, time blocked off, or all), and
 * whose — users and calendars, searchable, five of each until "See more" — and in what status. A rep sees their own
 * appointments only, so they get no user list. The list also takes days (`withDates`, ACL-598).
 */
export function ManageView({
  filters, onChange, onClose, withUsers, withDates,
}: {
  filters: ViewFilters;
  onChange: (next: ViewFilters) => void;
  onClose: () => void;
  withUsers: boolean;
  withDates: boolean;
}) {
  const [query, setQuery] = React.useState("");
  const needle = query.trim().toLowerCase();
  // The server searches the users (ACL-491); this filtered the first 50 by email, so nobody after
  // them could be picked. Somebody already picked is read on their own when the page loaded here
  // does not hold them, so they stay listed and can be unpicked.
  const people = useUserSearch(query, { is_active: true }, { enabled: withUsers });
  const loadedIds = new Set(people.users.map((u) => u.id));
  const pickedElsewhere = useUsersById(
    filters.userIds.filter((id) => !loadedIds.has(id)),
    // Not before the page is in: until then everybody picked looks missing from it.
    { enabled: withUsers && !people.loading },
  );
  const calendars = useCalendars();

  const toggle = (key: "userIds" | "calendarIds", id: number) =>
    onChange({
      ...filters,
      [key]: filters[key].includes(id) ? filters[key].filter((x) => x !== id) : [...filters[key], id],
    });

  const toOption = (u: { id: number; display_name: string | null; email: string }) => ({ id: u.id, label: staffName(u) });
  const pickedUsers = [...people.users, ...pickedElsewhere].filter((u) => filters.userIds.includes(u.id));
  const userOptions = withPicked(pickedUsers.map(toOption), people.users.map(toOption), people.searching, (o) => o.id);
  const calendarOptions = (calendars.data ?? [])
    .filter((c) => !needle || c.name.toLowerCase().includes(needle))
    .map((c) => ({ id: c.id, label: c.name }));

  return (
    <aside className="w-full shrink-0 space-y-4 rounded-xl border border-border bg-surface p-3 lg:w-72" aria-label="Manage view">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-text">Manage view</h2>
        <Button variant="ghost" size="icon-sm" aria-label="Close manage view" onClick={onClose}>
          <X />
        </Button>
      </div>

      <fieldset className="space-y-1.5 rounded-lg bg-gray-2 p-3">
        <legend className="sr-only">View by type</legend>
        <p className="pb-1 text-xs font-medium text-text">View by type</p>
        {(
          [
            ["all", "All"],
            ["callbacks", "Callbacks"],
            ["appointments", "Appointments"],
            ["blocks", "Blocked slots"],
          ] as const
        ).map(([value, label]) => (
          <label key={value} className="flex items-center gap-2 text-sm text-text">
            <input
              type="radio"
              name="view-type"
              className="size-3.5 accent-[var(--color-accent-9)]"
              checked={filters.type === value}
              onChange={() => onChange({ ...filters, type: value })}
            />
            {label}
          </label>
        ))}
      </fieldset>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <p className="text-xs font-medium text-text">Filters</p>
          {filterCount(filters, withDates) > 0 && (
            <button
              type="button"
              className="inline-flex items-center gap-1 text-xs text-accent-11 hover:underline"
              // Days the calendar view does not show stay for the list.
              onClick={() => onChange(withDates ? NO_FILTERS : { ...NO_FILTERS, from: filters.from, to: filters.to })}
            >
              <X className="size-3" aria-hidden />
              Clear all
            </button>
          )}
        </div>
        <Input
          leading={<Search className="size-3.5" />}
          placeholder={withUsers ? "Search users or calendars" : "Search calendars"}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        {withUsers && (
          <CheckList
            title="Users"
            options={userOptions}
            chosen={filters.userIds}
            onToggle={(id) => toggle("userIds", id)}
            searching={Boolean(needle)}
            total={people.total}
            loading={people.loading}
            footer={<UserSearchMore search={people} className="pl-1" />}
          />
        )}
        <CheckList
          title="Calendars"
          options={calendarOptions}
          chosen={filters.calendarIds}
          onToggle={(id) => toggle("calendarIds", id)}
          searching={Boolean(needle)}
        />
        {filters.type !== "blocks" && (
          <CheckList
            title="Status"
            options={ALL_STATUSES.map((s, i) => ({ id: i, ...statusInfo(s) }))}
            chosen={filters.statuses.map((s) => ALL_STATUSES.indexOf(s as AppointmentStatus))}
            onToggle={(i) => {
              const status = ALL_STATUSES[i]!;
              onChange({
                ...filters,
                statuses: filters.statuses.includes(status)
                  ? filters.statuses.filter((s) => s !== status)
                  : [...filters.statuses, status],
              });
            }}
            searching
          />
        )}
        {withDates && filters.type !== "blocks" && (
          <DateFilter range={filters} onChange={(range) => onChange({ ...filters, ...range })} />
        )}
      </div>
    </aside>
  );
}

/** The days the list is narrowed to (ACL-598): from one, to one, or between, both days in. */
function DateFilter({ range, onChange }: { range: DateRange; onChange: (next: DateRange) => void }) {
  const [open, setOpen] = React.useState(true);
  const set = Boolean(range.from || range.to);
  const Chevron = open ? ChevronUp : ChevronDown;
  return (
    <div className="space-y-1.5 border-t border-border pt-2">
      <div className="flex items-center justify-between">
        <button type="button" className="flex items-center gap-1.5 text-xs font-medium text-text" onClick={() => setOpen(!open)} aria-expanded={open}>
          <Chevron className="size-3.5" aria-hidden />
          Date range
          {set && <span className="rounded-full bg-accent-3 px-1.5 text-2xs text-accent-11">1</span>}
        </button>
        {set && (
          <button type="button" className="text-xs text-accent-11 hover:underline" onClick={() => onChange({ from: null, to: null })}>
            Clear dates
          </button>
        )}
      </div>
      {open && (
        <div className="grid grid-cols-2 gap-2 pl-1">
          <label className="space-y-1 text-xs text-text-muted">
            From
            <Input
              type="date"
              className="text-sm"
              value={range.from ?? ""}
              max={range.to ?? undefined}
              onChange={(e) => onChange({ ...range, from: e.target.value || null })}
            />
          </label>
          <label className="space-y-1 text-xs text-text-muted">
            To
            <Input
              type="date"
              className="text-sm"
              value={range.to ?? ""}
              min={range.from ?? undefined}
              onChange={(e) => onChange({ ...range, to: e.target.value || null })}
            />
          </label>
        </div>
      )}
    </div>
  );
}

function CheckList({
  title, options, chosen, onToggle, searching, total = null, loading = false, footer,
}: {
  title: string;
  /** `tone` puts the option's colour beside it: the statuses, so the list doubles as the
   *  calendar's key (ACL-525). */
  options: { id: number; label: string; tone?: Tone }[];
  chosen: number[];
  onToggle: (id: number) => void;
  searching: boolean;
  /** How many there are in all, when `options` are a page of them from the server. */
  total?: number | null;
  /** The server is still answering, so an empty list is not yet "None". */
  loading?: boolean;
  /** Under the whole list, once it is shown whole: how many of how many, and the next page. */
  footer?: React.ReactNode;
}) {
  const [open, setOpen] = React.useState(true);
  const [all, setAll] = React.useState(false);
  // The chosen ones first, so a filter in use is never hidden behind "See more".
  const ordered = [...options].sort((a, b) => Number(chosen.includes(b.id)) - Number(chosen.includes(a.id)));
  const shown = all || searching ? ordered : ordered.slice(0, SHOWN);
  const count = Math.max(ordered.length, total ?? 0);
  const Chevron = open ? ChevronUp : ChevronDown;
  return (
    <div className="space-y-1 border-t border-border pt-2">
      <button type="button" className="flex w-full items-center gap-1.5 text-xs font-medium text-text" onClick={() => setOpen(!open)} aria-expanded={open}>
        <Chevron className="size-3.5" aria-hidden />
        {title}
        {chosen.length > 0 && <span className="rounded-full bg-accent-3 px-1.5 text-2xs text-accent-11">{chosen.length}</span>}
      </button>
      {open && (
        <>
          {shown.length === 0 && <p className="pl-5 text-xs text-text-subtle">{loading ? "Searching…" : "None"}</p>}
          {shown.map((o) => (
            <label key={o.id} className={cn("flex items-center gap-2 pl-1 text-sm", chosen.includes(o.id) ? "text-text" : "text-text-muted")}>
              <input
                type="checkbox"
                className="size-3.5 accent-[var(--color-accent-9)]"
                checked={chosen.includes(o.id)}
                onChange={() => onToggle(o.id)}
              />
              {o.tone && <ToneDot tone={o.tone} />}
              <span className="truncate">{o.label}</span>
            </label>
          ))}
          {(all || searching) && footer}
          {!searching && count > SHOWN && (
            <button type="button" className="pl-1 text-xs font-medium text-accent-11 hover:underline" onClick={() => setAll(!all)}>
              {all ? "See less" : `See more (+${count - SHOWN})`}
            </button>
          )}
        </>
      )}
    </div>
  );
}
